import React, { useEffect } from 'react';

import type { SSRInput } from 'utils/ssr';
import { getSSRInput } from 'utils/ssr';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { ConsentBanner } from '@atlassian/browser-storage-controls/consent-banner';
import { initializeControls } from '@atlassian/browser-storage-controls/controllers/cookie-controls/initialize-controls';
import { getCookie } from '@atlassian/browser-storage-controls/controllers/cookie-controls/get-cookie';
import {
	ATLASSIAN_CONSENT_COOKIE_KEY,
	ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY,
} from '@atlassian/browser-storage-controls/constants';
import { shouldRenderBannerPrefetched } from '@atlassian/browser-storage-controls/controllers/banner-controls/should-banner-render-prefetched';
import type { PrefetchedConsentPreferenceData } from '@atlassian/browser-storage-controls/controllers/banner-controls/types';
// eslint-disable-next-line rulesdir/fedramp-isolated-cloud
import { isFedRamp } from '@atlassian/atl-context';

/**
 * Feature gate: csm-cm04-use-atlassian-browser-storage-controls
 *
 * Enables Atlassian-managed browser storage controls (webStorageEnabled) for the
 * CSM/JSM Help Center cookie consent banner.
 *
 * Note: registered as kebab-case in Statsig/Switcheroo; do not convert to snake_case.
 *
 * Rollout: BEARS-1324
 * Owner: @team-bears
 * Cleanup: FFCLEANUP-122701
 */
const CSM_CM04_USE_ATLASSIAN_BROWSER_STORAGE_CONTROLS =
	'csm-cm04-use-atlassian-browser-storage-controls';
const isCsmCm04UseAtlassianBrowserStorageControlsEnabled = (): boolean =>
	FeatureGates.checkGate(CSM_CM04_USE_ATLASSIAN_BROWSER_STORAGE_CONTROLS);

export const SSRSafeJSMCookieConsentBanner = () => {
	const ssrInput: SSRInput | undefined = getSSRInput();
	const consentToken = getCookie(ATLASSIAN_CONSENT_COOKIE_KEY) || ssrInput?.cookiesConsentToken;
	const fallbackConsentToken =
		getCookie(ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY) || ssrInput?.cookiesFallbackToken;

	const cookieData: PrefetchedConsentPreferenceData = consentToken
		? {
				consentToken,
				fallbackConsentToken,
			}
		: {
				fallbackConsentToken,
			};

	useEffect(() => {
		const perimeter: 'commercial' | 'fedramp-moderate' = isFedRamp()
			? 'fedramp-moderate'
			: 'commercial';

		if (!isCsmCm04UseAtlassianBrowserStorageControlsEnabled()) {
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			initializeControls({ product: 'jira' });
			return;
		}

		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		initializeControls({
			perimeter,
			product: 'jira',
			// Storage is enabled with webStorage + indexedDB consent enforcement always on.
			webStorageEnabled: true,
			indexedDbEnabled: true,
			disableWebStorageConsentChecks: false,
			disableIndexedDbConsentChecks: false,
			// Uncategorized web-storage/indexedDB keys are always blocked.
			allowUncategorizedItemsInWebStorageOverrides: false,
			allowUncategorizedItemsInIndexedDBOverrides: false,
		});
	}, []);

	// The case of only the fallbackConsentToken being present is handled differently on the
	// server and on the client. So in order to SSR the same way, we have handle this case
	// specially. Only fallback token being present on the client results in banner not being shown.
	// However, if we pass that cookie data into the initialPreferences on the client, there is a short
	// flicker of the consent banner. So we also have to special case this on the client.
	const onlyFallbackTokenPresent = Boolean(fallbackConsentToken && !consentToken);

	if (__SERVER__) {
		const shouldRenderBanner = onlyFallbackTokenPresent
			? false
			: shouldRenderBannerPrefetched(cookieData);
		if (shouldRenderBanner) {
			return (
				<div data-testid={'jsm-cookie-consent-banner-wrapper'}>
					<ConsentBanner product="jira" initialPreferences={cookieData} />
				</div>
			);
		}
		return <div data-testid={'jsm-cookie-consent-banner-wrapper'}></div>;
	}

	return (
		// Wrapper needed for stable DOM to rehydrate. Otherwise, rehydration failures may break the entire page when
		// this returns a banner in SSR and no banner/null in CSR (or the other way around).
		<div data-testid={'jsm-cookie-consent-banner-wrapper'}>
			{onlyFallbackTokenPresent ? (
				<ConsentBanner product="jira" />
			) : (
				<ConsentBanner product="jira" initialPreferences={cookieData} />
			)}
		</div>
	);
};
